import { useDynamicPageContext } from '@/contexts/DynamicPageContext';
import * as Sentry from '@sentry/react';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Input from '@superfunnel/ui-core/v2/Input';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import { updateVariant } from '../services/DynamicPageVariantsService';

interface Props {
  variantId: string | undefined;
  initialName: string | undefined;
  initialDescription: string | undefined;
  initialPath: string | undefined;
  open: boolean;
  onClose: () => void;
}

export default function EditVariantNameAndDescriptionModal({
  variantId,
  initialName,
  initialDescription,
  initialPath,
  open,
  onClose,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage, variants } = useDynamicPageContext();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [path, setPath] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    setName(initialName || '');
    setDescription(initialDescription || '');
    setPath(initialPath || '');
  }, [initialName, initialDescription, initialPath]);

  useEffect(() => {
    if (open) return;

    setSaving(false);
    setError(undefined);
  }, [open]);

  // Validate path: must be unique across variants (excluding current), and cannot be just "v"
  const pathError = useMemo(() => {
    const trimmedPath = path.trim();
    if (!trimmedPath) return undefined;

    // Cannot be just "v" (reserved for variant path prefix)
    if (trimmedPath === 'v') {
      return 'Path cannot be just "v" (reserved)';
    }

    // Check uniqueness across sibling variants
    const existingPaths = variants
      ?.filter((v) => v.id !== variantId)
      .map((v) => v.data().path)
      .filter(Boolean);

    if (existingPaths?.includes(trimmedPath)) {
      return 'This path is already used by another variant';
    }

    // Basic path validation: no slashes, no special characters
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmedPath)) {
      return 'Path can only contain letters, numbers, hyphens, and underscores';
    }

    return undefined;
  }, [path, variants, variantId]);

  const saveNameAndDescription = useCallback(async () => {
    if (saving) return;
    if (!variantId) return;
    if (!selectedAccount || !selectedDynamicPage) return;
    if (pathError) return;

    setSaving(true);

    try {
      const trimmedPath = path.trim();
      const update = {
        name: name,
        description: description,
        path: trimmedPath || null,
      };
      await updateVariant(
        selectedAccount.id,
        selectedDynamicPage.id,
        variantId,
        update,
      );
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setSaving(false);
  }, [
    saving,
    variantId,
    selectedAccount,
    selectedDynamicPage,
    name,
    description,
    path,
    pathError,
    onClose,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: 'Update',
        type: 'primary',
        loading: saving,
        disabled: !!pathError,
        onClick: () => saveNameAndDescription(),
      },
    ],
    [saving, pathError, onClose, saveNameAndDescription],
  );

  // Build preview URL for the path hint
  const pathPreview = useMemo(() => {
    const trimmedPath = path.trim();
    if (!trimmedPath) return null;
    const campaignPath = selectedDynamicPage?.data().path || 'campaign-path';
    return `${campaignPath}/v/${trimmedPath}`;
  }, [path, selectedDynamicPage]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="lg"
      title={'Update variant details'}
    >
      <div className="flex flex-col gap-4">
        <Input
          title="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextArea
          title="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div>
          <Input
            title="Path (optional)"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="e.g., mobile-users"
          />
          {pathError && (
            <p className="mt-1 text-sm text-red-600">{pathError}</p>
          )}
          {pathPreview && !pathError && (
            <p className="mt-1 text-sm text-gray-500">
              Variant URL: .../{pathPreview}
            </p>
          )}
          {!path.trim() && (
            <p className="mt-1 text-sm text-gray-400">
              Add a sub-path to access this variant directly via URL (useful for
              ad campaigns)
            </p>
          )}
        </div>
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to update variant'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
